import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Gamepad2,
  Loader2,
  CheckCircle2,
  XCircle,
  Trophy,
  Clock,
  Zap,
  Star,
  Medal,
  Crown
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';

const BACKEND_URL = window.location.origin;

// Join Screen Component
function JoinScreen({ sessionCode, onJoin }) {
  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    if (!name.trim()) {
      toast.error('Digite seu nome');
      return;
    }

    setJoining(true);
    try {
      const response = await api.post(`/quiz-live/sessions/${sessionCode}/join`, {
        session_code: sessionCode,
        player_name: name.trim()
      });
      onJoin(response.data.player_id, name.trim());
    } catch (error) {
      console.error('Error joining:', error);
      toast.error(error.response?.data?.detail || 'Erro ao entrar na sessão');
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/5 border-white/10">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-green-500 flex items-center justify-center mx-auto mb-6">
            <Gamepad2 className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">DNA Quiz Live</h1>
          <p className="text-gray-400 mb-6">Código: <span className="text-purple-400 font-bold">{sessionCode}</span></p>
          
          <div className="space-y-4">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome ou apelido"
              className="bg-white/10 border-white/20 text-white text-center text-lg h-14"
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              data-testid="player-name-input"
              autoFocus
            />
            
            <Button
              onClick={handleJoin}
              disabled={joining || !name.trim()}
              className="w-full h-14 text-lg bg-gradient-to-r from-purple-600 to-green-500 hover:from-purple-700 hover:to-green-600"
              data-testid="join-game-btn"
            >
              {joining ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Entrar no Jogo
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Waiting Screen Component
function WaitingScreen({ playerName }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold animate-pulse">
          {playerName.charAt(0).toUpperCase()}
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{playerName}</h2>
        <p className="text-gray-400 mb-6">Você está na sala!</p>
        <div className="flex items-center justify-center gap-2 text-green-400">
          <CheckCircle2 className="w-5 h-5" />
          <span>Aguardando o professor iniciar...</span>
        </div>
      </div>
    </div>
  );
}

// Question Screen Component
function QuestionScreen({ question, questionIndex, totalQuestions, timeLimit, onAnswer }) {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answered, setAnswered] = useState(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    setTimeLeft(timeLimit);
    setSelectedOption(null);
    setAnswered(false);
    startTimeRef.current = Date.now();

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!answered) {
            handleTimeout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [question.id]);

  const handleTimeout = () => {
    if (!answered) {
      setAnswered(true);
      onAnswer(null, timeLimit);
    }
  };

  const handleSelectOption = (option) => {
    if (answered) return;
    
    setSelectedOption(option.id);
    setAnswered(true);
    
    const timeTaken = (Date.now() - startTimeRef.current) / 1000;
    onAnswer(option.id, timeTaken);
  };

  const optionColors = [
    'from-red-500 to-red-600',
    'from-blue-500 to-blue-600',
    'from-yellow-500 to-yellow-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-pink-500 to-pink-600'
  ];

  const progress = (timeLeft / timeLimit) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-slate-900/80">
        <div className="flex items-center justify-between mb-3">
          <Badge variant="outline" className="border-white/30 text-white">
            {questionIndex + 1} / {totalQuestions}
          </Badge>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            timeLeft <= 5 ? 'bg-red-500 animate-pulse' : 'bg-white/10'
          }`}>
            <Clock className="w-4 h-4 text-white" />
            <span className="text-white font-bold text-lg">{timeLeft}s</span>
          </div>
        </div>
        <Progress 
          value={progress} 
          className="h-2 bg-white/20"
        />
      </div>

      {/* Question */}
      <div className="flex-1 flex flex-col p-4">
        <div className="flex-1 flex items-center justify-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white text-center max-w-3xl">
            {question.question}
          </h2>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-auto">
          {question.options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => handleSelectOption(option)}
              disabled={answered}
              className={`p-4 md:p-6 rounded-2xl text-white font-semibold text-lg md:text-xl transition-all transform active:scale-95 ${
                answered && selectedOption === option.id
                  ? 'ring-4 ring-white scale-105'
                  : answered
                  ? 'opacity-50'
                  : 'hover:scale-105'
              } bg-gradient-to-r ${optionColors[index % optionColors.length]}`}
              data-testid={`option-btn-${index}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold">
                  {String.fromCharCode(65 + index)}
                </span>
                <span className="flex-1 text-left">{option.text}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {answered && !selectedOption && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-center">
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white">Tempo esgotado!</h3>
          </div>
        </div>
      )}
    </div>
  );
}

// Answer Result Screen
function AnswerResultScreen({ isCorrect, points, correctOptionId, explanation }) {
  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isCorrect 
        ? 'bg-gradient-to-br from-green-900 to-green-950' 
        : 'bg-gradient-to-br from-red-900 to-red-950'
    }`}>
      <div className="text-center">
        {isCorrect ? (
          <>
            <CheckCircle2 className="w-24 h-24 text-green-400 mx-auto mb-4 animate-bounce" />
            <h2 className="text-3xl font-bold text-white mb-2">Correto!</h2>
            <div className="flex items-center justify-center gap-2 text-yellow-400 text-2xl font-bold">
              <Zap className="w-6 h-6" />
              +{points} pontos
            </div>
          </>
        ) : (
          <>
            <XCircle className="w-24 h-24 text-red-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-2">Incorreto</h2>
            <p className="text-gray-300">Não foi dessa vez!</p>
          </>
        )}
        
        {explanation && (
          <div className="mt-6 p-4 bg-white/10 rounded-xl max-w-md mx-auto">
            <p className="text-white text-sm">{explanation}</p>
          </div>
        )}
        
        <p className="text-white/60 mt-6 text-sm">
          Aguardando próxima pergunta...
        </p>
      </div>
    </div>
  );
}

// Final Results Screen
function FinalResultsScreen({ rankings, playerId }) {
  const playerRank = rankings.findIndex(p => p.id === playerId) + 1;
  const playerData = rankings.find(p => p.id === playerId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4">
      <div className="max-w-lg mx-auto">
        {/* Podium for top 3 */}
        <div className="flex items-end justify-center gap-2 mb-8 mt-8">
          {/* 2nd Place */}
          {rankings[1] && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center mx-auto mb-2 text-white text-xl font-bold">
                {rankings[1].name.charAt(0)}
              </div>
              <div className="bg-gray-500 rounded-t-xl p-4 h-24 w-24 flex flex-col items-center justify-end">
                <Medal className="w-6 h-6 text-white mb-1" />
                <span className="text-white font-bold text-sm truncate w-full">
                  {rankings[1].name}
                </span>
                <span className="text-white/80 text-xs">{rankings[1].score}pts</span>
              </div>
            </div>
          )}
          
          {/* 1st Place */}
          {rankings[0] && (
            <div className="text-center">
              <Crown className="w-8 h-8 text-yellow-400 mx-auto mb-1 animate-bounce" />
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center mx-auto mb-2 text-white text-2xl font-bold ring-4 ring-yellow-300">
                {rankings[0].name.charAt(0)}
              </div>
              <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-t-xl p-4 h-32 w-28 flex flex-col items-center justify-end">
                <Trophy className="w-8 h-8 text-white mb-1" />
                <span className="text-white font-bold truncate w-full">
                  {rankings[0].name}
                </span>
                <span className="text-white/80 text-sm">{rankings[0].score}pts</span>
              </div>
            </div>
          )}
          
          {/* 3rd Place */}
          {rankings[2] && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center mx-auto mb-2 text-white text-xl font-bold">
                {rankings[2].name.charAt(0)}
              </div>
              <div className="bg-orange-700 rounded-t-xl p-4 h-20 w-24 flex flex-col items-center justify-end">
                <Medal className="w-5 h-5 text-white mb-1" />
                <span className="text-white font-bold text-sm truncate w-full">
                  {rankings[2].name}
                </span>
                <span className="text-white/80 text-xs">{rankings[2].score}pts</span>
              </div>
            </div>
          )}
        </div>

        {/* Your Result */}
        {playerData && (
          <Card className="bg-gradient-to-r from-purple-600 to-green-500 border-0 mb-6">
            <CardContent className="p-6 text-center">
              <p className="text-white/80 text-sm mb-1">Sua posição</p>
              <p className="text-4xl font-black text-white mb-2">#{playerRank}</p>
              <div className="flex items-center justify-center gap-4 text-white">
                <div>
                  <p className="text-2xl font-bold">{playerData.score}</p>
                  <p className="text-xs text-white/80">pontos</p>
                </div>
                <div className="w-px h-10 bg-white/30" />
                <div>
                  <p className="text-2xl font-bold">{playerData.correct_answers}</p>
                  <p className="text-xs text-white/80">acertos</p>
                </div>
                <div className="w-px h-10 bg-white/30" />
                <div>
                  <p className="text-2xl font-bold">
                    {playerData.total_answers > 0 
                      ? Math.round((playerData.correct_answers / playerData.total_answers) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-white/80">aproveitamento</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Full Ranking */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <h3 className="text-white font-semibold mb-4">Ranking Completo</h3>
            <div className="space-y-2">
              {rankings.map((player, index) => (
                <div
                  key={player.id}
                  className={`flex items-center gap-3 p-3 rounded-xl ${
                    player.id === playerId
                      ? 'bg-purple-600/30 border border-purple-500'
                      : 'bg-white/5'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-yellow-500 text-white' :
                    index === 1 ? 'bg-gray-400 text-white' :
                    index === 2 ? 'bg-orange-600 text-white' :
                    'bg-white/20 text-white'
                  }`}>
                    {index + 1}
                  </span>
                  <span className="flex-1 text-white font-medium truncate">
                    {player.name}
                  </span>
                  <span className="text-purple-400 font-bold">
                    {player.score}pts
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Main Component
export default function QuizLivePlay() {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  
  const [gameState, setGameState] = useState('join'); // join, waiting, question, result, finished
  const [playerId, setPlayerId] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [answerResult, setAnswerResult] = useState(null);
  const [rankings, setRankings] = useState([]);

  const handleJoin = (id, name) => {
    setPlayerId(id);
    setPlayerName(name);
    setGameState('waiting');
    connectWebSocket(id);
  };

  const connectWebSocket = (pId) => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/quiz-live/ws/player/${sessionCode}/${pId}`);
    
    ws.onopen = () => {
      console.log('Player WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Player WS message:', data);
      
      if (data.type === 'game_started' || data.type === 'next_question') {
        setCurrentQuestion(data.question);
        setQuestionIndex(data.question_index);
        setTotalQuestions(data.total_questions);
        setAnswerResult(null);
        setGameState('question');
      }
      
      if (data.type === 'show_answer') {
        // Host showed answer, but we already have result from API
      }
      
      if (data.type === 'game_finished') {
        setRankings(data.rankings);
        setGameState('finished');
      }
    };
    
    ws.onclose = () => {
      console.log('Player WebSocket disconnected');
    };
    
    wsRef.current = ws;
    
    // Keep-alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  };

  const handleAnswer = async (optionId, timeTaken) => {
    if (!optionId) {
      // Timeout - show as incorrect
      setAnswerResult({
        is_correct: false,
        points: 0,
        explanation: null
      });
      setGameState('result');
      return;
    }

    try {
      const response = await api.post(
        `/quiz-live/sessions/${sessionCode}/answer?player_id=${playerId}`,
        {
          question_id: currentQuestion.id,
          option_id: optionId,
          time_taken: timeTaken
        }
      );
      
      setAnswerResult(response.data);
      setGameState('result');
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Erro ao enviar resposta');
    }
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Render based on game state
  if (gameState === 'join') {
    return <JoinScreen sessionCode={sessionCode} onJoin={handleJoin} />;
  }

  if (gameState === 'waiting') {
    return <WaitingScreen playerName={playerName} />;
  }

  if (gameState === 'question' && currentQuestion) {
    return (
      <QuestionScreen
        question={currentQuestion}
        questionIndex={questionIndex}
        totalQuestions={totalQuestions}
        timeLimit={currentQuestion.time_limit || 30}
        onAnswer={handleAnswer}
      />
    );
  }

  if (gameState === 'result' && answerResult) {
    return (
      <AnswerResultScreen
        isCorrect={answerResult.is_correct}
        points={answerResult.points}
        correctOptionId={answerResult.correct_option_id}
        explanation={answerResult.explanation}
      />
    );
  }

  if (gameState === 'finished') {
    return <FinalResultsScreen rankings={rankings} playerId={playerId} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );
}
